/**
 * Die Buchungsbelege einer Station — Ableitung, Weg und Schreibsperre.
 *
 * ## Warum das eine eigene Datei ist und keine Zeile im Markup
 *
 * Dieselbe Begründung wie bei `booking.ts` und `planner/adressen.ts`: Die
 * Tests laufen ohne Browser, und ein Vorlagenausdruck in einer `.svelte`-Datei
 * ist für sie unerreichbar. Was hier steht, ist genau die Art Arbeit, die
 * still falsch sein kann — eine Zeile in der Anhangliste, deren beide Knöpfe
 * ins Leere führen, sieht vollkommen richtig aus, bis jemand darauf klickt.
 *
 * ## Die Liste ist nur lesend, und das ist eine Regel des Servers
 *
 * `StopIn` kennt `anhaenge` nicht (siehe `main.py`): Dateiverweise erfinden,
 * vertauschen oder reihenweise löschen soll niemand nebenbei können, während
 * er eine Nacht verlängert. Geschrieben wird die Liste ausschließlich über die
 * drei Anhang-Endpunkte, und jeder von ihnen fasst genau eine Station an.
 *
 * Für diesen Client heißt das: `anhaenge` kommt beim Lesen mit und geht beim
 * Schreiben **nicht** mit. Der Server würde das Feld ohnehin verwerfen — es
 * trotzdem hier auszusortieren (`ohneAnhaenge`) ist der Unterschied zwischen
 * einer Zusicherung und einem Zufall. Wer die Liste eines Tages doch in
 * `StopIn` aufnähme, hätte sonst zwei Schreibwege auf dieselben Dateien.
 */

import { groesse } from './tagebuch';

/** Ein Beleg an der Station — genau die vier Felder, die der Server führt. */
export interface Anhang {
  /** UUID der Datei auf der Platte, 32 Hexziffern. Sie bildet den Abrufweg. */
  id: string;
  /** Der Anzeigename, wie er beim Hochladen mitkam — gekürzt und gesäubert. */
  name: string;
  bytes: number;
  /** Immer `application/pdf`; in v1 nimmt der Server nichts anderes an. */
  typ: string;
}

/** Eine Zeile der Anhangliste, fertig zum Hinschreiben. */
export interface Anhangzeile {
  id: string;
  name: string;
  /** `214 KB` — oder `null`, wenn keine brauchbare Größe dabeisteht. */
  groesse: string | null;
  /** Der Abrufweg am eigenen Server. */
  weg: string;
}

/**
 * Die Form der Kennung: 32 Hexziffern, wie `uuid4().hex` sie liefert.
 *
 * Geprüft wird sie, weil `stops.anhaenge` eine JSON-Spalte ist und darin alles
 * stehen kann, was je einmal hineingeschrieben wurde. Eine Zeile ohne
 * brauchbare Kennung ließe sich weder öffnen noch entfernen — sie wäre ein
 * Eintrag mit zwei toten Knöpfen, und das ist schlechter als kein Eintrag.
 */
const KENNUNG = /^[0-9a-f]{32}$/;

/**
 * Der Weg zur Datei am eigenen Server.
 *
 * Ohne `/api`-Präfix: Das setzt der Client erst beim Abruf (siehe
 * `api.anhangHolen`), und in diese Zeichenkette gehört der Weg, den der Server
 * in seiner Antwort selbst nennt (`/anhang/<id>.pdf`).
 */
export function anhangWeg(id: string): string {
  return `/anhang/${id}.pdf`;
}

/**
 * Aus dem, was am Stop hängt, die Zeilen der Liste machen.
 *
 * Nachsichtig gegenüber der Spalte, streng gegenüber der Kennung: Ein fehlender
 * Name wird zu `Anhang.pdf` (derselbe Ersatz, den der Server vergibt), eine
 * fehlende Größe zu `null` — aber ein Eintrag ohne brauchbare Kennung fällt
 * heraus, siehe `KENNUNG`.
 */
export function anhangzeilen(roh: readonly Anhang[] | null | undefined): Anhangzeile[] {
  if (!Array.isArray(roh)) return [];
  const zeilen: Anhangzeile[] = [];
  for (const e of roh) {
    if (!e || typeof e !== 'object') continue;
    const id = typeof e.id === 'string' ? e.id.trim().toLowerCase() : '';
    if (!KENNUNG.test(id)) continue;
    const name = typeof e.name === 'string' ? e.name.trim() : '';
    const bytes = typeof e.bytes === 'number' && Number.isFinite(e.bytes) ? e.bytes : null;
    zeilen.push({
      id,
      name: name || 'Anhang.pdf',
      groesse: bytes === null ? null : groesse(bytes),
      weg: anhangWeg(id)
    });
  }
  return zeilen;
}

/**
 * Der Buchungslink, sofern er einer ist — sonst `null`.
 *
 * Der Server lässt nur `http`/`https` in die Spalte (`_buchung_url_pruefen`),
 * und trotzdem wird hier noch einmal gefragt. Nicht aus Misstrauen gegen ihn,
 * sondern weil dieser Wert als `href` in die Seite geht: Ein Feld, dessen
 * Inhalt ungeprüft zum anklickbaren Ziel wird, ist genau die Stelle, an der
 * ein `javascript:` aus einer alten Zeile oder einem zweiten Schreibweg
 * wirksam würde. Die Prüfung kostet nichts und steht dort, wo sie wirkt.
 *
 * Was hier durchfällt, verschwindet **nur als Link**: Im Eingabefeld bleibt
 * der Text stehen, damit man sieht, was drinsteht, und ihn berichtigen kann.
 */
export function buchungslink(roh: string | null | undefined): string | null {
  const text = (roh ?? '').trim();
  if (!text) return null;
  return /^https?:\/\//i.test(text) ? text : null;
}

/**
 * Eine Station in der Fassung, die zum Server geht: ohne `anhaenge`.
 *
 * Die Begründung steht im Kopf dieser Datei. Eine Kopie und kein `delete` am
 * Original: Die Liste im Browser wird weiterbenutzt, und ihr die Anhänge
 * wegzunehmen, nur weil gerade gespeichert wird, ließe die Belege aus der
 * Oberfläche verschwinden, bis die Reise neu geladen wird.
 */
export function ohneAnhaenge<T extends object>(stop: T): T {
  const kopie = { ...stop } as T & { anhaenge?: unknown };
  delete kopie.anhaenge;
  return kopie;
}
